import { useQuery } from '@tanstack/react-query';
import type { StockItemView } from '@wodi/shared';
import { SearchPicker, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { searchStockItems } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './stock.module.css';

const MIN_CHARS = 2;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

/**
 * Type-to-search for a stock item (DESIGN.md §7): 44px rows with name, code and base
 * unit. Items not yet set up for stock are shown, labelled, and can't be chosen.
 */
export function StockItemPicker({
  label = 'Item',
  onSelect,
  autoFocus,
  allowUnconfigured = false,
}: {
  label?: string;
  onSelect: (item: StockItemView) => void;
  autoFocus?: boolean;
  /** For setting items up: items without stock details can be chosen too. */
  allowUnconfigured?: boolean;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, 250);
  const search = useQuery({
    queryKey: ['stock-item-search', debounced],
    queryFn: () => searchStockItems(debounced),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';

  return (
    <SearchPicker
      label={label}
      placeholder="Name or item code"
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data ?? []}
      itemsQuery={debounced}
      getKey={(item) => item.itemId}
      getTextValue={(item) => `${item.name} ${item.code}`}
      renderItem={(item) => (
        <span className={styles.pickerRow}>
          <span className={styles.pickerName}>{item.name}</span>
          <span className="wodi-id">{item.code}</span>
          <span className={styles.pickerMeta}>
            {item.stock === null
              ? 'Not set up for stock yet'
              : item.stock.packUnits
                  .filter((p) => p.status === 'active')
                  .map(
                    (p) =>
                      `${p.display} (${p.baseUnitsPerPack} ${item.stock?.baseUnit.display ?? ''})`,
                  )
                  .join(', ') || `Counted in ${item.stock.baseUnit.display}`}
          </span>
        </span>
      )}
      isExactMatch={(item, query) => item.code.toUpperCase() === query.toUpperCase()}
      onSelect={(item) => {
        if (item.stock === null && !allowUnconfigured) return;
        setText('');
        onSelect(item);
      }}
      promptMessage="Type at least two letters of the item name, or its code."
      emptyMessage="No stock item matches. Check the spelling, or add the item to the catalogue first."
      {...(search.error === null
        ? {}
        : {
            errorMessage:
              search.error instanceof ApiError
                ? search.error.message
                : 'Search did not work. Try again.',
          })}
      onRetry={() => void search.refetch()}
      offlineMessage="Searching items needs a connection to Wodi."
      countMessage={(n) => `${n} ${n === 1 ? 'item' : 'items'} found`}
      {...(autoFocus === undefined ? {} : { autoFocus })}
    />
  );
}
